import AccessVerification from "@/components/auth/AccessVerification";
import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import * as config from "@/config";
import { useAPI } from "@/contexts/APIContext";
import { getAuthErrorMessage } from "@/utils/auth";
import { useEffect, useState } from "react";
import { APIError, Const } from "snudbud-backbone-sdk";

// Define types for Google Identity API
interface CredentialResponse {
  credential: string;
  select_by: string;
  clientId: string;
}

interface GoogleAccountsType {
  id: {
    initialize: (config: {
      client_id: string;
      callback: (response: CredentialResponse) => void;
      auto_select?: boolean;
    }) => void;
    renderButton: (
      element: HTMLElement,
      options: {
        type?: string;
        theme?: string;
        size?: string;
        shape?: string;
        text?: string;
        logo_alignment?: string;
        width?: string;
      }
    ) => void;
  };
}

// Extend Window interface to include google property
declare global {
  interface Window {
    google?: {
      accounts: GoogleAccountsType;
    };
  }
}

/**
 * Login page component that handles user authentication
 */
export default function Login() {
  const [error, setError] = useState("");
  const { api } = useAPI();
  const [codeSent, setCodeSent] = useState(false);
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);

  const clearError = () => {
    setError("");
  };

  const handleError = (err: any) => {
    // Check if it has code and fields properties (APIError)
    if (err?.code && err?.fields) {
      // Let the component handle field errors
      // We only need to set the general error message if there is one
      if (err.message) setError(err.message);
    } else {
      // Regular error handling for non-field errors
      setError(getAuthErrorMessage(err));
    }
  };

  // Load Google Identity API script
  useEffect(() => {
    // Only load if Google Sign-In is visible
    if (codeSent) return;

    const loadGoogleScript = () => {
      // Check if script is already added
      if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
        setIsGoogleScriptLoaded(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => setIsGoogleScriptLoaded(true);
      document.body.appendChild(script);
    };

    loadGoogleScript();

    // Cleanup
    return () => {
      // Optional: remove the script when component unmounts
      const script = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [codeSent]);

  // Initialize Google Sign-In button when script is loaded
  useEffect(() => {
    if (!isGoogleScriptLoaded || codeSent) return;

    // Make sure google object is available
    if (window.google && window.google.accounts) {
      try {
        window.google.accounts.id.initialize({
          client_id: config.GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
        });

        const buttonElement = document.getElementById("google-signin-button");
        if (buttonElement) {
          window.google.accounts.id.renderButton(buttonElement, {
            type: "standard",
            theme: "outline",
            size: "large",
            shape: "rectangular",
            text: "signin_with",
            logo_alignment: "left",
            width: "100%",
          });
        }
      } catch (err) {
        console.error("Error initializing Google Sign-In:", err);
        setError("Failed to initialize Google Sign-In");
      }
    }
  }, [isGoogleScriptLoaded, codeSent]);

  const handleAccessVerificationComplete = async ({
    emailOrPhone,
    token,
    type,
  }: {
    emailOrPhone: string;
    token: string;
    type: Const.AccessVerificationType;
  }) => {
    clearError();
    try {
      if (type === Const.accessVerificationType.signIn) {
        await api.authenticate({ email: emailOrPhone, token });
      } else if (type === Const.accessVerificationType.signUp) {
        await api.user.create({ email: emailOrPhone, token });
      }
    } catch (err: any) {
      handleError(err);
    }
  };

  const handleGoogleCredentialResponse = async (response: CredentialResponse) => {
    clearError();

    try {
      // The credential property contains the JWT token from Google
      if (!response || !response.credential) {
        throw new Error("No credential received from Google");
      }

      const googleIdentityToken = response.credential;

      // Pass the Google Identity token to your backend
      await api.authenticate({ googleIdentityToken }).catch((err) => {
        if (err.code !== APIError.NotFoundCode.UserNotFound) throw err;
        return api.user.create({ googleIdentityToken });
      });
    } catch (err: any) {
      handleError(err);
    }
  };

  const handleAccessVerificationStep = ({
    emailOrPhone,
    error: verificationError,
  }: {
    emailOrPhone: string | undefined;
    error?: string;
  }) => {
    if (verificationError) {
      setError(verificationError);
    } else {
      clearError();
    }
    setCodeSent(!!emailOrPhone);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <Header>
        <div className="flex-1" />
      </Header>

      <div className="h-8" />

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Create Account or Sign In</h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>}

          <AccessVerification
            channel="email"
            onComplete={handleAccessVerificationComplete}
            onStep={handleAccessVerificationStep}
          />

          {!codeSent && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div className="mt-6">
                <div id="google-signin-button" className="w-full flex justify-center"></div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
